import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@/store';
import { performSessionLogout } from '@/utils/sessionManager';

/**
 * Custom hook for authentication
 * Provides auth state and methods for authentication operations
 */
export const useAuth = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  
  const { user, isAuthenticated, loading, error } = useAppSelector((state) => state.auth);

  /**
   * Handle logout
   */
  const handleLogout = async () => {
    await performSessionLogout(dispatch, 'manual', { showToast: false, navigate: false });
    navigate('/login');
  };

  /**
   * Check if user has specific role
   */
  const hasRole = (role: string): boolean => {
    return user?.role === role;
  };

  /**
   * Check if user has one of the specified roles
   */
  const hasAnyRole = (roles: string[]): boolean => {
    return roles.some((role) => user?.role === role);
  };

  /**
   * Check if user is admin
   */
  const isAdmin = (): boolean => {
    return user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  };

  /**
   * Check if user is project manager
   */
  const isProjectManager = (): boolean => {
    return user?.role === 'MANAGER' || user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  };

  return {
    user,
    isAuthenticated,
    loading,
    error,
    logout: handleLogout,
    hasRole,
    hasAnyRole,
    isAdmin,
    isProjectManager,
  };
};
